import React, { useState, useEffect } from 'react';
import './Journey.css';

const Journey = ({ onBack, onNavigateToDashboard, onNavigateToReflect, onNavigateToZenPlay }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [moodData, setMoodData] = useState([]);
  const [conversationInsights, setConversationInsights] = useState([]);
  const [error, setError] = useState('');
  const [personalityInsights, setPersonalityInsights] = useState('');
  const [totalDays, setTotalDays] = useState(0);

  const GEMINI_API_KEY = 'AIzaSyBx2UReN2SQOFFcDKG2Ngy-4elMNb7GnXs';
  const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`;

  // Load all user data from localStorage
  useEffect(() => {
    const loadUserData = () => {
      try {
        // Load reflection data
        const savedReflectionData = localStorage.getItem('reflectionData');
        // Load panda companion conversation data
        const savedConversationHistory = localStorage.getItem('pandaConversationHistory');
        // Load user wellness data
        const savedUserData = localStorage.getItem('moodSyncUserData');
        // Load Dashboard mood data
        const savedDashboardMoods = localStorage.getItem('dashboardMoodHistory');
        
        let allMoodData = [];
        let allConversations = [];
        
        // Process reflection data
        if (savedReflectionData) {
          const reflectionData = JSON.parse(savedReflectionData);
          Object.entries(reflectionData).forEach(([date, data]) => {
            if (data.mood !== undefined) {
              allMoodData.push({
                date,
                mood: data.mood,
                moodEmoji: getMoodEmoji(data.mood),
                source: 'reflection',
                learning: data.learning,
                gratitude: data.gratitude
              });
            }
          });
        }
        
        // Process dashboard mood data (from mood slider)
        if (savedDashboardMoods) {
          const dashboardMoods = JSON.parse(savedDashboardMoods);
          dashboardMoods.forEach(entry => {
            allMoodData.push({
              date: entry.date,
              mood: entry.mood,
              moodEmoji: getMoodEmoji(entry.mood),
              source: 'dashboard'
            });
          });
        }
        
        // Process conversation history
        if (savedConversationHistory) {
          const conversations = JSON.parse(savedConversationHistory);
          setConversationInsights(conversations);
        }
        
        // Sort mood data by date and remove duplicates
        const uniqueMoodData = [];
        const seenDates = new Set();
        allMoodData
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .forEach(entry => {
            const dateKey = entry.date.split('T')[0]; // Get just the date part
            if (!seenDates.has(dateKey)) {
              seenDates.add(dateKey);
              uniqueMoodData.push({ ...entry, date: dateKey });
            }
          });
        
        setMoodData(uniqueMoodData.slice(0, 14)); // Last 14 days
        setTotalDays(uniqueMoodData.length);
        
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Failed to load your journey data.');
      }
    };

    loadUserData();
  }, []);

  const getMoodEmoji = (moodValue) => {
    const moodEmojis = ['😢', '😞', '😐', '🙂', '😊'];
    return moodEmojis[Math.min(Math.max(0, Math.floor(moodValue)), 4)];
  };

  const callGeminiAPI = async (prompt) => {
    try {
      const response = await fetch(GEMINI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: prompt }]
          }],
          generationConfig: {
            temperature: 0.7,
            topK: 1,
            topP: 1,
            maxOutputTokens: 1024
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text.trim();
      } else {
        throw new Error('Invalid response format from Gemini API');
      }
    } catch (error) {
      console.error('Error calling Gemini API:', error);
      throw error;
    }
  };

  const generateAISummary = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Gather all user data
      const reflectionData = JSON.parse(localStorage.getItem('reflectionData') || '{}');
      const conversationHistory = JSON.parse(localStorage.getItem('pandaConversationHistory') || '[]');
      const userData = JSON.parse(localStorage.getItem('moodSyncUserData') || '{}');
      
      if (moodData.length === 0 && conversationHistory.length === 0) {
        throw new Error('No data available to analyze. Start by tracking your mood or chatting with Zen!');
      }
      
      // Create comprehensive prompt for AI analysis
      let analysisPrompt = `As a compassionate mental health analyst, analyze this user's wellness journey and provide insights:\n\n`;
      
      // Add mood data
      if (moodData.length > 0) {
        analysisPrompt += `MOOD TRACKING DATA (Last ${moodData.length} days):\n`;
        moodData.forEach(entry => {
          analysisPrompt += `- ${entry.date}: ${entry.moodEmoji} (${entry.mood}/4)`;
          if (entry.learning) analysisPrompt += ` | Learning: ${entry.learning}`;
          if (entry.gratitude) analysisPrompt += ` | Gratitude: ${entry.gratitude}`;
          analysisPrompt += '\n';
        });
      }
      
      // Add conversation insights
      if (conversationHistory.length > 0) {
        analysisPrompt += `\nCONVERSATION THEMES (Recent topics discussed with Zen):\n`;
        conversationHistory.slice(-5).forEach((conv, i) => {
          analysisPrompt += `${i + 1}. ${conv.summary || conv.topic || 'General wellness discussion'}\n`;
        });
      }
      
      // Add user preferences if available
      if (userData.name) {
        analysisPrompt += `\nUSER PROFILE:\n`;
        analysisPrompt += `- Name: ${userData.name}\n`;
        if (userData.wellnessGoals) analysisPrompt += `- Goals: ${userData.wellnessGoals}\n`;
        if (userData.currentMood) analysisPrompt += `- Recent mood: ${userData.currentMood}\n`;
        if (userData.stressLevel) analysisPrompt += `- Stress level: ${userData.stressLevel}\n`;
      }
      
      analysisPrompt += `\nPlease provide a warm, personalized summary of their mental health journey including key patterns, strengths, and gentle encouragement. Keep the tone supportive and hopeful with appropriate emojis. IMPORTANT: Limit your response to exactly 50 words - no more, no less.`;
      
      const aiResponse = await callGeminiAPI(analysisPrompt);
      setSummary(aiResponse);
      
      // Also generate personality insights if we have enough data
      if (conversationHistory.length > 2 || moodData.length > 5) {
        const personalityPrompt = `Based on the data above, provide a brief personality insight about this person's emotional patterns, coping strategies, and growth areas. Keep it positive and constructive. Max 3 sentences.`;
        const personalityResponse = await callGeminiAPI(personalityPrompt);
        setPersonalityInsights(personalityResponse);
      }
      
    } catch (err) {
      console.error('Error generating AI summary:', err);
      setError(err.message || 'Failed to generate summary. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="journey-container">
      <div className="falling-leaves">
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
      </div>

      <div className="journey-content">
        <div className="journey-header">
          <h1>Your Journey 📈</h1>
          <p>Reflect on your mood patterns and insights</p>
        </div>

        <div className="journey-stats">
          <div className="stat-card">
            <div className="stat-number">{totalDays}</div>
            <div className="stat-label">Days Tracked</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{conversationInsights.length}</div>
            <div className="stat-label">Conversations</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {moodData.length > 0 ? 
                getMoodEmoji(moodData.reduce((sum, entry) => sum + entry.mood, 0) / moodData.length) : 
                '😐'
              }
            </div>
            <div className="stat-label">Avg Mood</div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="summary-section">
          <div className="section-header">
            <h2>🤖 AI-Generated Insights</h2>
            <p>Let Zen analyze your wellness journey</p>
          </div>
          
          <button 
            className={`generate-btn ${isLoading ? 'loading' : ''}`}
            onClick={generateAISummary}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Analyzing your journey...
              </>
            ) : (
              <>
                <span className="btn-icon">✨</span>
                Generate AI Summary
              </>
            )}
          </button>

          {summary && (
            <div className="ai-summary-card">
              <div className="summary-header">
                <span className="ai-badge">🤖 AI Analysis</span>
              </div>
              <div className="summary-content">
                {summary.split('\n').map((line, i) => {
                  line = line.trim();
                  if (!line) return <br key={i} />;
                  
                  if (line.match(/^\d+\./)) {
                    return <div key={i} className="summary-point">{line}</div>;
                  } else if (line.includes('**') || line.includes('*')) {
                    return <div key={i} className="summary-highlight">{line.replace(/\*\*/g, '').replace(/\*/g, '')}</div>;
                  } else {
                    return <p key={i} className="summary-text">{line}</p>;
                  }
                })}
              </div>
            </div>
          )}
          
          {personalityInsights && (
            <div className="personality-insights">
              <h3>💡 Personality Insights</h3>
              <p>{personalityInsights}</p>
            </div>
          )}
        </div>

        {moodData.length > 0 && (
          <div className="mood-analytics">
            <div className="section-header">
              <h2>📊 Mood Analytics</h2>
              <p>Your emotional journey over time</p>
            </div>
            
            {/* Mood Meter */}
            <div className="mood-meter-section">
              <h3>Current Mood Level</h3>
              <div className="mood-meter">
                <div className="meter-container">
                  <div 
                    className="meter-fill" 
                    style={{
                      width: `${((moodData[0]?.mood || 0) / 4) * 100}%`,
                      background: `linear-gradient(90deg, 
                        ${moodData[0]?.mood <= 1 ? '#ef4444' : 
                          moodData[0]?.mood <= 2 ? '#f97316' : 
                          moodData[0]?.mood <= 3 ? '#eab308' : '#22c55e'} 0%, 
                        ${moodData[0]?.mood <= 1 ? '#dc2626' : 
                          moodData[0]?.mood <= 2 ? '#ea580c' : 
                          moodData[0]?.mood <= 3 ? '#ca8a04' : '#16a34a'} 100%)`
                    }}
                  ></div>
                  <div className="meter-emoji">{moodData[0]?.moodEmoji || '😐'}</div>
                </div>
                <div className="meter-labels">
                  <span>Low</span>
                  <span>Moderate</span>
                  <span>High</span>
                </div>
              </div>
            </div>
            
            {/* Mood Timeline */}
            <div className="mood-timeline-section">
              <h3>14-Day Mood History</h3>
              <div className="mood-timeline">
                {moodData.slice(0, 14).map((entry, index) => (
                  <div key={index} className="mood-timeline-entry">
                    <div 
                      className="mood-bar" 
                      style={{
                        height: `${(entry.mood / 4) * 100}%`,
                        background: `linear-gradient(to top, 
                          ${entry.mood <= 1 ? '#ef4444' : 
                            entry.mood <= 2 ? '#f97316' : 
                            entry.mood <= 3 ? '#eab308' : '#22c55e'}, 
                          ${entry.mood <= 1 ? '#fca5a5' : 
                            entry.mood <= 2 ? '#fdba74' : 
                            entry.mood <= 3 ? '#fde047' : '#86efac'})`
                      }}
                    >
                      <div className="mood-tooltip">
                        <div className="tooltip-emoji">{entry.moodEmoji}</div>
                        <div className="tooltip-date">{new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                        {entry.learning && <div className="tooltip-learning">💡 {entry.learning}</div>}
                        {entry.gratitude && <div className="tooltip-gratitude">🙏 {entry.gratitude}</div>}
                      </div>
                    </div>
                    <div className="timeline-date">
                      {new Date(entry.date).toLocaleDateString('en-US', { 
                        weekday: 'short',
                        day: 'numeric' 
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Mood Distribution */}
            <div className="mood-distribution">
              <h3>Mood Distribution</h3>
              <div className="distribution-chart">
                {['😢', '😞', '😐', '🙂', '😊'].map((emoji, index) => {
                  const count = moodData.filter(entry => getMoodEmoji(entry.mood) === emoji).length;
                  const percentage = moodData.length > 0 ? (count / moodData.length) * 100 : 0;
                  return (
                    <div key={index} className="distribution-item">
                      <div className="distribution-emoji">{emoji}</div>
                      <div className="distribution-bar">
                        <div 
                          className="distribution-fill" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="distribution-count">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav">
        <div 
          className="nav-item" 
          onClick={onNavigateToDashboard || onBack}
        >
          <span className="nav-icon">🏠</span>
          <span className="nav-label">Home</span>
        </div>
        <div 
          className="nav-item"
          onClick={onNavigateToReflect}
        >
          <span className="nav-icon">💭</span>
          <span className="nav-label">Reflect</span>
        </div>
        <div 
          className="nav-item"
          onClick={onNavigateToZenPlay}
        >
          <span className="nav-icon">🎮</span>
          <span className="nav-label">Zen Play</span>
        </div>
        <div className="nav-item active">
          <span className="nav-icon">📈</span>
          <span className="nav-label">Journey</span>
        </div>
      </div>
    </div>
  );
};

export default Journey;
